/**
 * Phase 4.2a — Inbound WebRTC Routing settings (instrumentation only).
 *
 * Renders inside Settings → Communication when
 * `REACT_APP_WEBRTC_INBOUND_ENABLED=true`. This phase ships ZERO routing
 * changes — it only:
 *   1. Surfaces a team-presence table (who's currently online in
 *      browser, last seen, registered status).
 *   2. Lets owners/admins toggle per-user opt-in flags
 *      (`ringInBrowser`, `alsoRingPhone`) so they're already populated
 *      when Phase 4.2c wires the actual router.
 *   3. Lets a non-admin team member toggle their own row.
 *
 * Existing PSTN forwarding / simul-ring / voicemail / missed-call
 * text-back are completely untouched.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Globe, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ENABLED = process.env.REACT_APP_WEBRTC_INBOUND_ENABLED === 'true';

const formatLastSeen = (secs) => {
  if (secs === null || secs === undefined) return 'never';
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const fetchAuthHeaders = async () => {
  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error('Not signed in');
  return { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' };
};

export default function InboundWebRTCSettings() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingUid, setSavingUid] = useState('');
  const [meta, setMeta] = useState({ heartbeatSecs: 20, freshnessSecs: 45 });

  const refresh = useCallback(async () => {
    if (!ENABLED) return;
    setLoading(true);
    setError('');
    try {
      const headers = await fetchAuthHeaders();
      const res = await fetch(`${API}/api/webrtc/presence/team`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `failed (${res.status})`);
      setMembers(Array.isArray(body.members) ? body.members : []);
      setMeta({
        heartbeatSecs: body.heartbeatSecs || 20,
        freshnessSecs: body.freshnessSecs || 45,
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ENABLED) return undefined;
    refresh();
    // Auto-refresh every 30s so the "online" badges stay live without
    // requiring the user to click Refresh.
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const updatePref = async (uid, patch) => {
    setSavingUid(uid);
    // Optimistic update
    setMembers((prev) => prev.map((m) => (m.uid === uid ? { ...m, ...patch } : m)));
    try {
      const headers = await fetchAuthHeaders();
      const res = await fetch(`${API}/api/webrtc/presence/preferences`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ targetUid: uid, ...patch }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `failed (${res.status})`);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Could not save');
      // Roll back optimistic update
      refresh();
    } finally {
      setSavingUid('');
    }
  };

  if (!ENABLED) return null;

  return (
    <Card data-testid="inbound-webrtc-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-emerald-600" /> Inbound WebRTC routing
          <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-amber-800 text-[10px] uppercase tracking-wide">
            Beta · instrumentation
          </Badge>
        </CardTitle>
        <CardDescription>
          Track which team members are currently registered for browser-based ringing.
          PSTN forwarding, simul-ring, and voicemail remain unchanged. Per-user toggles
          here will start driving the inbound router in a later phase — set them now so
          they're ready.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <div
            className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800 flex gap-2 items-start"
            data-testid="inbound-webrtc-error"
          >
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        ) : null}

        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Heartbeat every {meta.heartbeatSecs}s · stale after {meta.freshnessSecs}s
          </span>
          <Button
            type="button" size="sm" variant="ghost"
            onClick={refresh}
            disabled={loading}
            data-testid="inbound-webrtc-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="inbound-webrtc-team-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="text-left font-medium py-2 px-3">Team member</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
                <th className="text-left font-medium py-2 px-3">Ring browser</th>
                <th className="text-left font-medium py-2 px-3">
                  Also ring phone
                  <span className="block text-[10px] font-normal text-slate-400">
                    when registered in browser
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-xs text-slate-500 py-4">
                    {loading ? 'Loading…' : 'No team members yet.'}
                  </td>
                </tr>
              ) : null}
              {members.map((m) => (
                <tr
                  key={m.uid}
                  className="border-t border-slate-100"
                  data-testid={`inbound-webrtc-row-${m.uid}`}
                >
                  <td className="py-2 px-3">
                    <div className="font-medium text-slate-900">{m.name}</div>
                    {m.email ? <div className="text-xs text-slate-500">{m.email}</div> : null}
                  </td>
                  <td className="py-2 px-3">
                    {m.online ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`inbound-webrtc-online-${m.uid}`}>
                        ● Online
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-slate-600">
                        Offline
                      </Badge>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">
                      seen {formatLastSeen(m.secondsSinceSeen)}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <Switch
                      checked={!!m.ringInBrowser}
                      disabled={savingUid === m.uid}
                      onCheckedChange={(v) => updatePref(m.uid, { ringInBrowser: v })}
                      data-testid={`inbound-webrtc-ring-browser-${m.uid}`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Switch
                      checked={!!m.alsoRingPhone}
                      disabled={savingUid === m.uid}
                      onCheckedChange={(v) => updatePref(m.uid, { alsoRingPhone: v })}
                      data-testid={`inbound-webrtc-also-phone-${m.uid}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
